import { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  Search, X, Loader2, CheckCircle2, ChevronLeft, ChevronRight,
  Phone, Mail, MapPin, Hash, ShieldCheck, ShieldAlert, Shield, ArrowUpRight,
  Truck, AlertCircle, AlertTriangle, XCircle, Database,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Field helpers ─────────────────────────────────────────────────────────────
const getMC  = (auth)   => auth?.find(a => a.type === 'MC_PRIMARY')?.number  || '';
const getDOT = (auth)   => auth?.find(a => a.type === 'US_DOT')?.number      || '';

const getPhone = (phones) => {
  for (const t of ['LISTING', 'BILLING', 'LOCAL']) {
    const p = phones?.find(ph => ph.type === t);
    if (p?.phoneNumber) return p.phoneNumber;
  }
  return phones?.[0]?.phoneNumber || '';
};

const getCityState = (addrs) => {
  const a = addrs?.find(a => a.type === 'LISTING') || addrs?.[0];
  return a ? [a.city, a.state].filter(Boolean).join(', ') : '';
};

const parseOffset = (url) => {
  if (!url) return 0;
  const m = url.match(/limit\((\d+),\d+\)/);
  return m ? parseInt(m[1], 10) : 0;
};

const EMPTY_FILTERS = { name: '', mcNumber: '', dotNumber: '', phoneNumber: '', email: '' };

// ── FMCSA validation detail (shown in drawer after import) ───────────────────
function FmcsaImportDetail({ check }) {
  if (!check) return null;

  const pass    = check.overall_pass;
  const unknown = pass === null || pass === undefined;

  const borderCls = pass === true  ? 'border-emerald-500/30 bg-emerald-500/[0.04]'
                  : pass === false ? 'border-red-500/30 bg-red-500/[0.04]'
                  : 'border-border bg-muted/30';

  const HeaderIcon = pass === true ? ShieldCheck : pass === false ? ShieldAlert : Shield;
  const iconCls    = pass === true ? 'text-emerald-500' : pass === false ? 'text-red-500' : 'text-muted-foreground';
  const badgeCls   = pass === true
    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
    : pass === false
    ? 'bg-red-500/10 text-red-600 dark:text-red-400'
    : 'bg-muted text-muted-foreground';
  const badgeLabel = pass === true ? 'PASSED' : pass === false ? 'FLAGS FOUND' : 'UNKNOWN';

  const tiles = [
    { label: 'Authority',    value: check.authority_status || '—',
      ok: check.authority_status === 'ACTIVE' },
    { label: 'Safety',       value: check.safety_rating   || 'Not Rated',
      ok: check.safety_rating === 'Satisfactory' || check.safety_rating === 'Not Rated' },
    { label: 'Insurance',    value: check.insurance_on_file == null ? '—' : check.insurance_on_file ? 'On File' : 'Not on File',
      ok: check.insurance_on_file !== false },
    { label: 'Out of Svc',  value: check.out_of_service == null ? '—' : check.out_of_service ? 'Yes' : 'No',
      ok: !check.out_of_service },
  ];

  const flagList = [
    { active: check.flag_no_authority, label: 'No Active Authority' },
    { active: check.flag_safety_risk,  label: `Safety Risk${check.safety_rating ? `: ${check.safety_rating}` : ''}` },
    { active: check.flag_no_insurance, label: 'No Insurance on File' },
    { active: check.flag_oos,          label: 'Out of Service' },
  ].filter(f => f.active != null);

  return (
    <div className={`mt-3 rounded-xl border p-3 space-y-2.5 ${borderCls}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <HeaderIcon className={`h-3.5 w-3.5 ${iconCls}`} strokeWidth={1.5} />
          <span className="text-xs font-semibold text-foreground">FMCSA Validation</span>
        </div>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${badgeCls}`}>{badgeLabel}</span>
      </div>

      {unknown ? (
        <p className="text-xs text-muted-foreground">No DOT number on file or carrier not found in FMCSA registry.</p>
      ) : (
        <>
          {/* 4-tile grid */}
          <div className="grid grid-cols-4 gap-1.5">
            {tiles.map(t => (
              <div key={t.label} className="bg-card border border-border rounded-lg p-2 text-center">
                <p className="text-[9px] text-muted-foreground mb-0.5">{t.label}</p>
                <p className={`text-[11px] font-bold leading-tight ${t.ok ? 'text-emerald-500' : 'text-red-500'}`}>
                  {t.value}
                </p>
              </div>
            ))}
          </div>

          {/* Flags */}
          {flagList.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {flagList.map(f => (
                <span key={f.label} className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                  f.active ? 'bg-red-500/10 text-red-600 dark:text-red-400' : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                }`}>
                  {f.active
                    ? <AlertTriangle className="h-2.5 w-2.5" />
                    : <CheckCircle2 className="h-2.5 w-2.5" />}
                  {f.label}
                </span>
              ))}
            </div>
          )}

          {/* Legal name if available */}
          {check.legal_name && (
            <p className="text-[10px] text-muted-foreground">
              Legal: <span className="text-foreground font-medium">{check.legal_name}</span>
              {check.carrier_operation && ` · ${check.carrier_operation}`}
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ── Carrier detail drawer ─────────────────────────────────────────────────────
function CarrierDrawer({ carrier, onClose, onImport, importing, importedId, wasAlreadyInSystem, fmcsaCheck }) {
  if (!carrier) return null;

  const auth   = carrier.customerAuthorityNumbers || [];
  const phones = carrier.phoneNumbers || [];
  const addrs  = carrier.addresses    || [];

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="w-full max-w-md bg-card border-l border-border flex flex-col shadow-2xl overflow-y-auto">
        <div className="flex items-start justify-between p-5 border-b border-border shrink-0">
          <div>
            <p className="text-xs text-muted-foreground font-mono mb-1">{carrier.customerId}</p>
            <h2 className="text-lg font-semibold text-foreground leading-tight">{carrier.customerName}</h2>
            {carrier.email && <p className="text-sm text-muted-foreground mt-0.5">{carrier.email}</p>}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {auth.length > 0 && (
          <Section icon={ShieldCheck} title="Authority Numbers">
            {auth.map((a, i) => (
              <div key={i} className="flex items-center justify-between py-1.5">
                <span className="text-xs text-muted-foreground font-mono">{a.type}</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-mono text-foreground">{a.number}</span>
                  {a.verified && <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />}
                </div>
              </div>
            ))}
          </Section>
        )}

        {phones.length > 0 && (
          <Section icon={Phone} title="Phone Numbers">
            {phones.map((p, i) => (
              <div key={i} className="flex items-center justify-between py-1.5">
                <span className="text-xs text-muted-foreground">{p.type}</span>
                <span className="text-sm text-foreground tabular-nums">
                  {p.phoneNumber}
                  {p.extension && <span className="text-muted-foreground"> x{p.extension}</span>}
                </span>
              </div>
            ))}
          </Section>
        )}

        {addrs.length > 0 && (
          <Section icon={MapPin} title="Addresses">
            {addrs.map((a, i) => (
              <div key={i} className="py-1.5">
                <p className="text-xs text-muted-foreground mb-0.5">{a.type}</p>
                <p className="text-sm text-foreground">
                  {[a.streetAddress, a.streetAddress2].filter(Boolean).join(', ')}
                </p>
                <p className="text-sm text-foreground">
                  {[a.city, a.state, a.zipcode].filter(Boolean).join(', ')}
                </p>
              </div>
            ))}
          </Section>
        )}

        {(carrier.ezrating || carrier.ezscore != null) && (
          <Section icon={Hash} title="Rating">
            {carrier.ezrating && (
              <div className="flex items-center justify-between py-1.5">
                <span className="text-xs text-muted-foreground">EZ Rating</span>
                <span className="text-sm font-medium text-foreground">{carrier.ezrating}</span>
              </div>
            )}
            {carrier.ezscore != null && (
              <div className="flex items-center justify-between py-1.5">
                <span className="text-xs text-muted-foreground">EZ Score</span>
                <span className="text-sm font-medium text-foreground">{carrier.ezscore}</span>
              </div>
            )}
          </Section>
        )}

        <div className="mt-auto p-5 border-t border-border shrink-0">
          {importedId ? (
            <>
              <div className="flex items-center justify-between">
                <div className={`flex items-center gap-2 text-sm font-medium ${wasAlreadyInSystem ? 'text-blue-500' : 'text-green-600 dark:text-green-400'}`}>
                  <Database className="h-4 w-4" />
                  {wasAlreadyInSystem ? 'Already in system' : 'Imported to database'}
                </div>
                <Link
                  to={`/admin/carriers/${importedId}`}
                  className="flex items-center gap-1.5 text-sm text-accent hover:underline font-medium"
                >
                  View Profile
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>
              {!wasAlreadyInSystem && <FmcsaImportDetail check={fmcsaCheck} />}
            </>
          ) : (
            <button
              onClick={() => onImport(carrier)}
              disabled={importing}
              className="w-full flex items-center justify-center gap-2 h-10 rounded-full
                bg-accent text-white text-sm font-semibold
                hover:bg-accent/90 active:scale-95 transition-all
                disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {importing ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Importing…</>
              ) : (
                <><Truck className="h-4 w-4" /> Import to Database</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="px-5 py-4 border-b border-border">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{title}</p>
      </div>
      {children}
    </div>
  );
}

// ── Shared input component ─────────────────────────────────────────────────────
function FilterInput({ label, value, onChange, placeholder, type = 'text', autoFocus, compact, icon: Icon }) {
  return compact ? (
    <div className="relative">
      {Icon && <Icon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" strokeWidth={1.5} />}
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={label}
        className={`w-full h-9 ${Icon ? 'pl-8' : 'pl-3'} pr-3 rounded-lg border border-border bg-background
          text-sm text-foreground placeholder:text-muted-foreground
          focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent`}
      />
    </div>
  ) : (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <input
        type={type}
        autoFocus={autoFocus}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || label}
        className="w-full h-11 px-4 rounded-xl border border-border bg-background
          text-sm text-foreground placeholder:text-muted-foreground
          focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent"
      />
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function CarrierSearchPage() {
  const { token } = useAuth();

  const [filters,    setFilters]    = useState(EMPTY_FILTERS);
  const [loading,    setLoading]    = useState(false);
  const [results,    setResults]    = useState(null);
  const [count,      setCount]      = useState(0);
  const [nextUrl,    setNextUrl]    = useState(null);
  const [searchError, setSearchError] = useState(null);

  const prevStack  = useRef([]);
  const [canGoBack, setCanGoBack]   = useState(false);
  const [selected,  setSelected]    = useState(null);
  const [importing, setImporting]   = useState(false);
  const [importedMap, setImportedMap] = useState({});   // customerId → carrier_id (imported this session)
  const [existingMap, setExistingMap] = useState({});   // customerId → carrier_id (already in system)
  const [fmcsaMap, setFmcsaMap]     = useState({});     // customerId → fmcsa_check summary
  const [selectedIds, setSelectedIds] = useState(new Set()); // for bulk import
  const [bulkImporting, setBulkImporting] = useState(false);

  const activeFilters = useRef(EMPTY_FILTERS);

  const hasAnyFilter = Object.values(filters).some(v => v.trim());
  const set = (key) => (val) => setFilters(f => ({ ...f, [key]: val }));

  const applyPage = useCallback((data) => {
    setResults(data.items || []);
    setNextUrl(data.next_url || null);
    setCount(data.count || 0);
  }, []);

  const doFetch = useCallback(async (cursorUrl) => {
    setLoading(true);
    try {
      const f = activeFilters.current;
      const params = new URLSearchParams();
      if (f.name.trim())        params.set('name',         f.name.trim());
      if (f.mcNumber.trim())    params.set('mc_number',    f.mcNumber.trim());
      if (f.dotNumber.trim())   params.set('dot_number',   f.dotNumber.trim());
      if (f.phoneNumber.trim()) params.set('phone_number', f.phoneNumber.trim());
      if (f.email.trim())       params.set('email',        f.email.trim());
      if (cursorUrl)            params.set('next_url',     cursorUrl);

      const res = await fetch(`${API}/api/admin/manheim/search?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      setSearchError(null);
      applyPage(data);
      setSelectedIds(new Set());

      // Check which of the returned carriers already exist in the system
      const ids = (data.items || []).map(c => c.customerId).filter(Boolean);
      if (ids.length) {
        fetch(`${API}/api/admin/carriers/check-by-manheim-ids`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ids }),
        }).then(r => r.ok ? r.json() : {}).then(existing => {
          setExistingMap(existing);
        }).catch(() => {});
      }

      return data.current_url;
    } catch (e) {
      setSearchError(e.message);
      setResults([]);
      setCount(0);
      setNextUrl(null);
      toast.error(`Search failed: ${e.message}`);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token, applyPage]);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!hasAnyFilter) return;
    activeFilters.current = { ...filters };
    prevStack.current = [];
    setCanGoBack(false);
    setSearchError(null);
    await doFetch(null);
  };

  const handleNext = async () => {
    if (!nextUrl) return;
    const cur = await doFetch(nextUrl);
    if (cur) { prevStack.current.push(cur); setCanGoBack(true); }
  };

  const handlePrev = async () => {
    if (!prevStack.current.length) return;
    const prev = prevStack.current.pop();
    setCanGoBack(prevStack.current.length > 0);
    await doFetch(prev);
  };

  const handleImport = async (carrier) => {
    setImporting(true);
    try {
      const res = await fetch(`${API}/api/admin/manheim/import`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ customer: carrier }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      setImportedMap(m => ({ ...m, [carrier.customerId]: data.carrier_id }));
      if (data.fmcsa_check) {
        setFmcsaMap(m => ({ ...m, [carrier.customerId]: data.fmcsa_check }));
      }
      toast.success(`${carrier.customerName} imported successfully.`);
    } catch (e) {
      toast.error(`Import failed: ${e.message}`);
    } finally {
      setImporting(false);
    }
  };

  const isInSystem = (customerId) => !!(importedMap[customerId] || existingMap[customerId]);
  const getCarrierId = (customerId) => importedMap[customerId] || existingMap[customerId];

  const toggleSelect = (customerId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(customerId)) next.delete(customerId); else next.add(customerId);
      return next;
    });
  };

  const notImported = (results || []).filter(c => !isInSystem(c.customerId));
  const allSelected = notImported.length > 0 && notImported.every(c => selectedIds.has(c.customerId));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(notImported.map(c => c.customerId)));
    }
  };

  const handleBulkImport = async () => {
    const toImport = (results || []).filter(c => selectedIds.has(c.customerId));
    if (!toImport.length) return;
    setBulkImporting(true);
    let success = 0, fail = 0;
    for (const carrier of toImport) {
      try {
        const res = await fetch(`${API}/api/admin/manheim/import`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ customer: carrier }),
        });
        if (!res.ok) { fail++; continue; }
        const data = await res.json();
        setImportedMap(m => ({ ...m, [carrier.customerId]: data.carrier_id }));
        if (data.fmcsa_check) setFmcsaMap(m => ({ ...m, [carrier.customerId]: data.fmcsa_check }));
        success++;
      } catch { fail++; }
    }
    setSelectedIds(new Set());
    setBulkImporting(false);
    if (fail === 0) toast.success(`${success} carrier${success !== 1 ? 's' : ''} imported successfully.`);
    else toast.warning(`${success} imported, ${fail} failed.`);
  };

  const af = activeFilters.current;
  const activeLabel = [
    af.name,
    af.mcNumber    && `MC: ${af.mcNumber}`,
    af.dotNumber   && `DOT: ${af.dotNumber}`,
    af.phoneNumber,
    af.email,
  ].filter(Boolean).join(', ');

  const offset = results && nextUrl ? parseOffset(nextUrl) - (results?.length ?? 0) : null;
  const from   = offset != null ? offset + 1 : results ? 1 : 0;
  const to     = results ? from + results.length - 1 : 0;
  const hasResults = results !== null;

  return (
    <div className="space-y-6">

      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-foreground tracking-tight">Carrier Search</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Search the Manheim / Central Dispatch directory and import carriers
          </p>
        </div>
        <Link
          to="/admin/carriers"
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          All Carriers
        </Link>
      </div>

      {!hasResults ? (
        /* ── Hero search ── */
        <div className="flex flex-col items-center justify-center py-16 gap-6">
          <div className="flex flex-col items-center gap-2 text-center">
            <div className="h-14 w-14 rounded-2xl bg-accent/10 flex items-center justify-center mb-2">
              <Search className="h-7 w-7 text-accent" strokeWidth={1.5} />
            </div>
            <h2 className="text-xl font-semibold text-foreground">Search the Directory</h2>
            <p className="text-sm text-muted-foreground max-w-sm">
              Search by company name, MC#, DOT#, phone, or email — any combination works
            </p>
          </div>

          <form onSubmit={handleSearch} className="w-full max-w-xl space-y-3">
            <FilterInput
              label="Company Name"
              value={filters.name}
              onChange={set('name')}
              placeholder="e.g. Acme Transport, tex, tra…"
              autoFocus
            />
            <div className="grid grid-cols-2 gap-3">
              <FilterInput label="MC #"    value={filters.mcNumber}    onChange={set('mcNumber')}    placeholder="e.g. MC-001493" />
              <FilterInput label="DOT #"   value={filters.dotNumber}   onChange={set('dotNumber')}   placeholder="e.g. 2576414" />
              <FilterInput label="Phone #" value={filters.phoneNumber} onChange={set('phoneNumber')} placeholder="e.g. 8065355636" />
              <FilterInput label="Email"   value={filters.email}       onChange={set('email')}       placeholder="e.g. info@carrier.com" type="email" />
            </div>
            <button
              type="submit"
              disabled={loading || !hasAnyFilter}
              className="w-full h-11 rounded-xl bg-accent text-white text-sm font-semibold
                hover:bg-accent/90 active:scale-95 transition-all
                disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Search
            </button>
          </form>
        </div>
      ) : (
        /* ── Compact search + results ── */
        <>
          <form onSubmit={handleSearch} className="rounded-xl border border-border bg-card p-3 space-y-2">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              <FilterInput compact label="Company Name" value={filters.name}        onChange={set('name')}        icon={Search} />
              <FilterInput compact label="MC #"         value={filters.mcNumber}    onChange={set('mcNumber')}    icon={ShieldCheck} />
              <FilterInput compact label="DOT #"        value={filters.dotNumber}   onChange={set('dotNumber')}   icon={Hash} />
              <FilterInput compact label="Phone #"      value={filters.phoneNumber} onChange={set('phoneNumber')} icon={Phone} />
              <FilterInput compact label="Email"        value={filters.email}       onChange={set('email')}       icon={Mail} type="email" />
              <button
                type="submit"
                disabled={loading || !hasAnyFilter}
                className="h-9 px-5 rounded-lg bg-accent text-white text-sm font-semibold
                  hover:bg-accent/90 active:scale-95 transition-all
                  disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
              >
                {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Search
              </button>
            </div>
          </form>

          {/* Results */}
          {loading ? (
            <div className="flex items-center justify-center py-20 gap-3 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Searching…</span>
            </div>
          ) : searchError ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <AlertCircle className="h-8 w-8 text-destructive/50" strokeWidth={1.5} />
              <p className="text-sm font-medium text-foreground">(0) records found</p>
              <p className="text-xs text-muted-foreground max-w-sm text-center">{searchError}</p>
            </div>
          ) : results.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3 text-muted-foreground">
              <Search className="h-8 w-8 opacity-30" strokeWidth={1.5} />
              <p className="text-sm font-medium text-foreground">No records found</p>
              <p className="text-xs text-muted-foreground">No carriers matched "{activeLabel}"</p>
            </div>
          ) : (
            <>
            {selectedIds.size > 0 && (
              <div className="flex items-center justify-between px-4 py-2.5 rounded-xl border border-accent/30 bg-accent/5">
                <span className="text-sm font-medium text-foreground">
                  {selectedIds.size} carrier{selectedIds.size !== 1 ? 's' : ''} selected
                </span>
                <button
                  onClick={handleBulkImport}
                  disabled={bulkImporting}
                  className="flex items-center gap-2 h-8 px-4 rounded-full bg-accent text-white text-xs font-semibold
                    hover:bg-accent/90 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {bulkImporting
                    ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Importing…</>
                    : <><Truck className="h-3.5 w-3.5" /> Bulk Import {selectedIds.size}</>}
                </button>
              </div>
            )}

            <div className="rounded-xl border border-border overflow-hidden">
              <div className="grid grid-cols-[32px_2fr_1fr_1fr_1fr_1.5fr_auto] gap-3 px-4 py-2.5
                bg-muted/40 border-b border-border text-xs font-medium text-muted-foreground uppercase tracking-wide">
                <div className="flex items-center justify-center">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    className="h-3.5 w-3.5 rounded border-border accent-accent cursor-pointer"
                    title="Select all unimported"
                  />
                </div>
                <span>Company</span>
                <span>MC #</span>
                <span>DOT #</span>
                <span>Phone</span>
                <span>City / State</span>
                <span className="text-right pr-1">Status</span>
              </div>

              {results.map((c) => {
                const mc        = getMC(c.customerAuthorityNumbers);
                const dot       = getDOT(c.customerAuthorityNumbers);
                const phone     = getPhone(c.phoneNumbers);
                const cityState = getCityState(c.addresses);
                const inSystem  = isInSystem(c.customerId);
                const carrierId = getCarrierId(c.customerId);
                const justImported = !!importedMap[c.customerId];
                const alreadyExisted = !justImported && !!existingMap[c.customerId];
                const isChecked = selectedIds.has(c.customerId);

                return (
                  <div
                    key={c.customerId}
                    className={`grid grid-cols-[32px_2fr_1fr_1fr_1fr_1.5fr_auto] gap-3 px-4 py-3
                      border-b border-border last:border-0 transition-colors group
                      ${isChecked ? 'bg-accent/5' : 'hover:bg-muted/30'}`}
                  >
                    <div className="flex items-center justify-center">
                      {inSystem ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                      ) : (
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleSelect(c.customerId)}
                          className="h-3.5 w-3.5 rounded border-border accent-accent cursor-pointer"
                        />
                      )}
                    </div>

                    <button onClick={() => setSelected(c)} className="text-left">
                      <p className="text-sm font-medium text-foreground group-hover:text-accent transition-colors truncate leading-snug">
                        {c.customerName}
                      </p>
                      <p className="text-[11px] text-muted-foreground font-mono mt-0.5 truncate">
                        {c.customerId}
                      </p>
                    </button>

                    <span className="text-sm text-muted-foreground font-mono self-center truncate">
                      {mc || <span className="opacity-30">—</span>}
                    </span>
                    <span className="text-sm text-muted-foreground font-mono self-center truncate">
                      {dot || <span className="opacity-30">—</span>}
                    </span>
                    <span className="text-sm text-muted-foreground self-center tabular-nums truncate">
                      {phone || <span className="opacity-30">—</span>}
                    </span>
                    <span className="text-sm text-muted-foreground self-center truncate">
                      {cityState || <span className="opacity-30">—</span>}
                    </span>

                    <div className="self-center flex items-center justify-end gap-2">
                      {inSystem ? (
                        <div className="flex items-center gap-2">
                          <span className={`flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                            justImported
                              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                              : 'bg-blue-500/10 text-blue-500'
                          }`}>
                            <Database className="h-2.5 w-2.5" />
                            {justImported ? 'Imported' : 'In System'}
                          </span>
                          <Link
                            to={`/admin/carriers/${carrierId}`}
                            className="flex items-center gap-1 text-xs text-accent hover:underline font-medium"
                            onClick={e => e.stopPropagation()}
                          >
                            View
                            <ArrowUpRight className="h-3 w-3" />
                          </Link>
                        </div>
                      ) : (
                        <button
                          onClick={() => setSelected(c)}
                          className="text-xs text-accent hover:underline font-medium"
                        >
                          Import
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          

          {/* Pagination footer */}
          {results.length > 0 && (
            <div className="flex items-center justify-between pt-1">
              <p className="text-sm text-muted-foreground">
                {count > 0 ? (
                  <>Showing <span className="font-medium text-foreground">{results.length}</span> of{' '}
                  <span className="font-medium text-foreground">{count.toLocaleString()}</span> results</>
                ) : (
                  <><span className="font-medium text-foreground">{results.length}</span> results on this page</>
                )}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrev}
                  disabled={!canGoBack || loading}
                  className="flex items-center gap-1.5 h-8 px-4 rounded-lg border border-border
                    text-sm font-medium text-muted-foreground
                    hover:text-foreground hover:bg-muted transition-colors
                    disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Prev
                </button>
                <button
                  onClick={handleNext}
                  disabled={!nextUrl || loading}
                  className="flex items-center gap-1.5 h-8 px-4 rounded-lg border border-border
                    text-sm font-medium text-muted-foreground
                    hover:text-foreground hover:bg-muted transition-colors
                    disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
            </>
          )}
        </>
      )}

      <CarrierDrawer
        carrier={selected}
        onClose={() => setSelected(null)}
        onImport={handleImport}
        importing={importing}
        importedId={selected ? getCarrierId(selected.customerId) : null}
        wasAlreadyInSystem={selected ? !!existingMap[selected.customerId] && !importedMap[selected.customerId] : false}
        fmcsaCheck={selected ? fmcsaMap[selected.customerId] : null}
      />
    </div>
  );
}
